import { useEffect, useRef } from 'react';

interface Props {
  onClickOutside: (event: MouseEvent) => void;
  enabled?: boolean;
  ignoreSelectors?: string[];
}

export default function useClickOutside<T extends HTMLElement = HTMLDivElement>({
  enabled = true,
  onClickOutside,
  ignoreSelectors = [], // ['[role="dialog"]', '.modal', '.dropdown-menu']
}: Props) {
  const containerRef = useRef<T>(null);
  const onClickOutsideRef = useRef(onClickOutside);
  const ignoreSelectorsRef = useRef(ignoreSelectors);

  onClickOutsideRef.current = onClickOutside;
  ignoreSelectorsRef.current = ignoreSelectors;

  useEffect(() => {
    if (!enabled) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;

      if (containerRef.current && !containerRef.current.contains(target)) {
        const hasIgnoreElement = ignoreSelectorsRef.current.some(selector => {
          try {
            // selector가 유효하고, target이 해당 selector의 자손이면 null이 아님
            return target.closest(selector) !== null;
          } catch {
            // '#" 같은 유효하지 않은 selector로 인한 SyntaxError 방지
            console.warn('Invalid ignoreSelector in useClickOutside:', selector);
          }
          return false;
        });

        if (!hasIgnoreElement) {
          onClickOutsideRef.current(event);
        }
      }
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside, true);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [enabled]);

  return containerRef;
}
